import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiCheckCircle, FiXCircle, FiX, FiPlusCircle } from "react-icons/fi";

export default function AddProgramGroup({ theme }) {
  const [programGroup, setProgramGroup] = useState("");
  const [toast, setToast] = useState({ type: "", message: "" });

  useEffect(() => {
    if (toast.message) {
      const timer = setTimeout(() => setToast({ type: "", message: "" }), 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setToast({ type: "", message: "" });

    if (!programGroup.trim()) {
      setToast({ type: "error", message: "Program group name is required." });
      return;
    }

    const payload = {
      name: programGroup.trim()
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/create-programme-group`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json",Authorization: `Bearer ${localStorage.getItem("token")}`  },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setToast({ type: "success", message: data.message || "Program group created successfully!" });
        setProgramGroup("");
      } else {
        setToast({ type: "error", message: data.message || "Failed to create program group." });
      }
    } catch (error) {
      console.error("Full fetch error:", error);
      setToast({ type: "error", message: "Network error. Please try again." });
    }
  };

  return (
    <div className="relative bg-white rounded-3xl shadow-xl p-8 max-w-xl mx-auto mt-10">
      {/* Toast Notifications */}
      <div className="fixed top-6 right-6 z-50">
        <AnimatePresence>
          {toast.message && (
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.3 }}
              className={`flex items-center max-w-xs p-4 border rounded-lg shadow-lg space-x-3 overflow-hidden
                ${toast.type === "error"
                  ? "bg-red-50 border-red-300 text-red-700"
                  : "bg-green-50 border-green-300 text-green-700"}`}
            >
              <div className="shrink-0">
                {toast.type === "error" ? <FiXCircle size={20} /> : <FiCheckCircle size={20} />}
              </div>
              <div className="flex-1 text-sm leading-snug">{toast.message}</div>
              <button
                onClick={() => setToast({ type: "", message: "" })}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                aria-label="Close"
              >
                <FiX size={16} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Header */}
      <motion.h2
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2"
      >
        <FiPlusCircle className="text-blue-600" size={24} />
        Add New Programme Group
      </motion.h2>

      {/* Form */}
      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        className="space-y-6"
      >
        <div>
          <label htmlFor="programGroup" className="block text-gray-700 font-medium mb-2">
            Programme Group Name
          </label>
          <input
            id="programGroup"
            type="text"
            value={programGroup}
            onChange={(e) => setProgramGroup(e.target.value)}
            placeholder="e.g. Computer Science BSc"
            required
            className="w-full border border-gray-300 rounded-lg px-5 py-3 text-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300"
          >
            Add Programme Group
          </button>
        </div>
      </motion.form>
    </div>
  );
}
